import { useNavigation } from "@react-navigation/native";
import _orderBy from "lodash/orderBy";
import { useCallback, useMemo, useState } from "react";
import { FlatList, ListRenderItem } from "react-native";

import {
  Callout,
  CalloutRefresh,
  StatusListItem,
  VideoListItem,
  VideoPlayer,
} from "../components";
import { Button, Col } from "../lib";
import {
  useUser,
  useUserVideos,
  UserVideo,
  useUserStatuses,
  UserStatus,
} from "../services";
import { LoadingScreen } from "./loading";

type MediaItem = UserVideo | UserStatus;

function getMediaItems(
  statuses: UserStatus[],
  videos: UserVideo[],
): MediaItem[] {
  return _orderBy<MediaItem>(
    [...statuses, ...videos],
    [
      (item) =>
        typeof item.created_at === "number"
          ? item.created_at
          : Date.parse(String(item.created_at)) || 0,
    ],
    ["desc"],
  );
}

export function TimelineScreen() {
  // Data
  const {
    data: user,
    isLoading: isUserLoading,
    refetch: refetchUser,
  } = useUser();

  const {
    data: videos,
    isLoading: isVideosLoading,
    invalidate: invalidateVideos,
  } = useUserVideos();

  const {
    data: statuses,
    isLoading: isStatusesLoading,
    invalidate: invalidateStatuses,
  } = useUserStatuses();

  // State
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);

  // Vars
  const mediaItems: MediaItem[] | null = useMemo(() => {
    if (!!statuses && !!videos) {
      return getMediaItems(statuses, videos);
    }

    return null;
  }, [statuses, videos]);

  // Handlers
  const navigation = useNavigation();

  const handleCreate = useCallback(() => {
    navigation.navigate("TimelineCreate");
  }, [navigation]);

  const handlePressVideo = useCallback(async (url: string) => {
    setPlayingUrl(url);
  }, []);

  const handleClosePlayer = useCallback(() => {
    setPlayingUrl(null);
  }, []);

  const renderItem: ListRenderItem<MediaItem> = ({ item }) => {
    // Video item
    if ((item as UserVideo).url) {
      const v = item as UserVideo;
      return (
        <VideoListItem
          id={v.id}
          url={v.url}
          thumbnail={v.thumbnail}
          createdAt={v.created_at}
          processed={v.processed}
          onPress={handlePressVideo}
          onDelete={invalidateVideos}
        />
      );
    }

    // Status item
    const s = item as UserStatus;
    return (
      <StatusListItem
        approved={s.approved}
        createdAt={Date.parse(s.created_at) || 0}
        id={s.id}
        text={s.text}
        imageUrl={s.image_url}
        onDelete={invalidateStatuses}
      />
    );
  };

  // Render
  if (isUserLoading || isVideosLoading || isStatusesLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <CalloutRefresh onRefresh={refetchUser} />;
  }

  const header = (
    <Col m="4">
      <Button icon="pencil" onPress={handleCreate}>
        Post status
      </Button>
    </Col>
  );

  return (
    <>
      <FlatList
        data={mediaItems}
        ListEmptyComponent={
          <Callout icon="video" message="You have no videos yet" />
        }
        renderItem={renderItem}
        ListHeaderComponent={header}
      />

      <VideoPlayer url={playingUrl} onClose={handleClosePlayer} />
    </>
  );
}
